import { createReducer } from 'redux-starter-kit';
import { handleError } from '../../modules/utils/handleError';
import i18next from 'i18next';
import { VoucherApplicationsApi } from './VoucherApplicationsApi';
import * as Dict from '../../modules/utils/Dict';
import Notice from '../../modules/utils/Notice';
import { NCALayerSign } from '../../components/NCALayer/NCALayerSign';
import { history } from '../../_helpers/history';
import paths from '../../_helpers/paths';

/**
 * Constants
 */

export const voucherApplicationsModule = 'voucherApplications';
const SET_DATA = `${voucherApplicationsModule}/SET_DATA`;
const LOADING = `${voucherApplicationsModule}/LOADING`;
const CLEAR_STATE = `${voucherApplicationsModule}/CLEAR_STATE`;

/**
 * Reducer
 */

const initialState = {
  loading: true,
  data: [],
  pageNumber: 1,
  totalElements: 0
};

export default createReducer(initialState, {
  [SET_DATA]: (state, action) => {
    state.data = action.data;
    state.pageNumber = action.pageNumber;
    state.totalElements =
      action.pageNumber === 1 ? action.totalElements : state.totalElements;
  },
  [LOADING]: (state, action) => {
    state.loading = action.loading;
  },
  [CLEAR_STATE]: () => initialState
});

export const clearState = () => ({ type: CLEAR_STATE });

const getKeyValues = data => {
  let res = [];
  data.forEach(item => {
    let obj = {};
    item.passportValues.forEach(a => {
      obj[a.code] = a.value;
    });
    res.push(obj);
  });
  return res;
};
export const canGetVoucher = (data, push) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    let filter = {
      locationCode: data.queuedRegion,
      statusWeb: data.appStatus === 'IN_QUEUED' ? 'available_all' : null,
      childBirthDay: data.childBirthday,
      queuedType: data.queuedType,
      rehabType: data.rehabType,
      correctionType: data.correctionType
    };
    const response = await VoucherApplicationsApi.getBulletins(filter);
    if (response.data.content) {
      let res = getKeyValues(response.data.content);
      let check = false;
      for (let i = 0; i < res.length; i++) {
        if (res[i].available_sits > 0) {
          check = true;
          break;
        }
      }

      if (check) {
        push(
          `/bulletin-open-places/voucherId=${data.id}?filter=${JSON.stringify({
            pageNumber: 1,
            pageSize: 10,
            locationCode: data.queuedRegion,
            statusWeb: data.appStatus === 'IN_QUEUED' ? 'available_all' : null,
            childBirthDay: data.childBirthday,
            queuedType: data.queuedType,
            rehabType: data.rehabType,
            correctionType: data.correctionType
          })}`
        );
      } else {
        Notice.error(i18next.t('voucher_applications_check_bulletin_empty'));
        return false;
      }
    } else {
      Notice.error(i18next.t('voucher_applications_check_bulletin_error'));
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_check_bulletin_error'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};
export const loadApplications = filter => async dispatch => {
  try {
    const { data } = await VoucherApplicationsApi.getApplications(JSON.stringify(filter));
    for (let row of data.result.content) {
      row.providerLocation = await Dict.item('60', row.queuedRegion);
    }
    if (data.status === 'SUCCESS') {
      const content = [];
      for (let item of data.result.content) {
        let queuedType = item.queuedType
          ? await Dict.item('gu_queued_type', item.queuedType || '')
          : '';
        let appStatusWeb = item.appStatusWeb
          ? await Dict.item('gu_app_status_web', item.appStatusWeb || '')
          : '';
        let priorityQueued = item.priorityQueued
          ? await Dict.item('priority_queued', item.priorityQueued || '')
          : '';
        let priorityCategory = item.priorityCategory
          ? await Dict.item('priority_category', item.priorityCategory || '')
          : '';

        content.push({
          ...item,
          queuedTypeStatus: queuedType,
          appStatusWebStatus: appStatusWeb,
          priorityQueuedStatus: priorityQueued,
          priorityCategory: priorityCategory,
          canGetNapravlenie: false
        });
      }
      dispatch({
        type: SET_DATA,
        data: content,
        pageNumber: data.result.pageNumber,
        totalElements: data.result.totalElements
      });
    } else {
      Notice.error(i18next.t('voucher_applications_errorLoad'));
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_errorLoad'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};
export const revokeByApplicant = (id, modal, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const { data } = await VoucherApplicationsApi.getSignString({
      id,
      appStatus: 'LEFT_QUEUED'
    });
    const signature = await NCALayerSign(data.result.signature);
    if (signature) {
      await VoucherApplicationsApi.revokeByApplicantApi({
        id: id,
        appStatus: 'LEFT_QUEUED',
        signature: signature
      });
      dispatch(loadApplications(filter));
      modal.close();
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_error_revoke_applicant'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const approveVoucherByApplicant = (id, modal) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const { data } = await VoucherApplicationsApi.getSignString({
      id: id,
      appStatus: 'RECEIVED_VOUCHER',
      bulletinId: modal.data.id
    });
    const signature = await NCALayerSign(data.result.signature);
    if (signature) {
      await VoucherApplicationsApi.approveVoucher({
        id: id,
        appStatus: 'RECEIVED_VOUCHER',
        signature: signature,
        bulletinId: modal.data.id
      });
      modal.close();
      history.push(paths.voucherApplications);
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_error_approve_applicant'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};
export const confirmInterest = (id, modal, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    await VoucherApplicationsApi.confirmInterestApi({ id: id });
    dispatch(loadApplications(filter));
    modal.close();
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_error_confirm_interest'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const rejectVoucherByApplicant = (id, modal, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const { data } = await VoucherApplicationsApi.getSignString({
      id: id,
      appStatus: 'REJECTED_VOUCHER'
    });
    const signature = await NCALayerSign(data.result.signature);
    if (signature) {
      await VoucherApplicationsApi.rejectVoucher({
        id: id,
        appStatus: 'REJECTED_VOUCHER',
        signature: signature
      });
      dispatch(loadApplications(filter));
      modal.close();
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_error_reject_applicant'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const applicationDownload = ({ requestFormId }) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const response = await VoucherApplicationsApi.applicationDownload(requestFormId);
    download(
      response.data,
      `${i18next.t('voucher_applications_subtitle')}-${requestFormId}.pdf`
    );
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_errorLoad'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

const download = (data, fileName) => {
  let element = document.createElement('a');
  document.body.appendChild(element);
  element.style.display = 'none';
  let blob = new Blob([data]);
  let url = window.URL.createObjectURL(blob);
  element.href = url;
  element.download = fileName;
  element.click();
  window.URL.revokeObjectURL(url);
};
